import React from "react";
import { Popover } from "react-text-selection-popover";
import styled from "styled-components";

interface Props {
  children?: React.ReactNode;
  disabled?: boolean;
  targetElementRef: React.RefObject<HTMLElement>;
}

const OFFSET_TOP = 110;
const MOBILE_SELECT_MENU_HEIGHT = 40;
// rendered width of popover
const POPOVER_WIDTH = 376;
const POPOVER_MAX_HEIGHT = 304;

const SelectionPopover: React.FC<Props> = ({
  children,
  disabled,
  targetElementRef,
}) => {
  if (typeof document === "undefined") {
    return <></>;
  }

  return (
    <Popover
      render={({ clientRect: selectionRect, textContent }) => {
        if (disabled || !selectionRect) {
          return null;
        }

        const doc = document.documentElement;
        const scrollTop =
          (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

        // using let here since need to re-set value for left later
        let left = selectionRect.left + selectionRect.width / 2;
        const maxLeft = document.body.clientWidth - POPOVER_WIDTH / 2;
        if (left < POPOVER_WIDTH / 2) {
          left = POPOVER_WIDTH / 2;
        }
        if (left > maxLeft) {
          left = maxLeft;
        }

        // render Popover above the selected text
        let top =
          selectionRect.top +
          scrollTop -
          OFFSET_TOP - // create space from the top of the screen
          MOBILE_SELECT_MENU_HEIGHT; // create space for mobile text selection menu
        const minTop = scrollTop + OFFSET_TOP;
        const maxTop = scrollTop + window.innerHeight - POPOVER_MAX_HEIGHT;

        // render Popover below the selected text
        if (top < minTop) {
          top = Math.max(
            selectionRect.bottom + scrollTop + OFFSET_TOP,
            scrollTop + OFFSET_TOP
          );
        }

        const targetRect = targetElementRef?.current?.getBoundingClientRect();

        // Ensure text is selected in the area of targetRect.
        if (
          !targetRect ||
          !(
            targetRect.top <= selectionRect.top &&
            // format of targetRect.bottom is a float when selectionRect.bottom is an integer
            // round up targetRect.bottom to match selectionRect.bottom format
            selectionRect.bottom <= Math.ceil(targetRect?.bottom ?? 0) &&
            // ensure the selected text doesn't cross the left right side of the target
            targetRect.x - 1 <= selectionRect.x &&
            selectionRect.x < targetRect.x + targetRect.width + 1
          )
        ) {
          return null;
        }

        const selectedImgSrc = textContent
          ? undefined
          : document
              .elementFromPoint(selectionRect.x, selectionRect.y)
              ?.getAttribute("src");

        if (!textContent && !selectedImgSrc) {
          return null;
        }

        // Adjust position when selection target is image
        if (selectedImgSrc) {
          const middleTop =
            scrollTop + selectionRect.top + selectionRect.height / 2;
          top = middleTop < minTop ? minTop : middleTop;
          top = top > maxTop ? maxTop : top;
        }

        return (
          <Container left={left} top={top}>
            {children}
          </Container>
        );
      }}
    />
  );
};

interface ContainerAttrs {
  left: number;
  top: number;
}

const Container = styled.div.attrs<ContainerAttrs>(
  (attrs) => attrs
)<ContainerAttrs>`
  -webkit-user-select: none;
  background: #fff;
  border-radius: 5px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  left: ${({ left }) => left}px;
  padding: 24px;
  position: absolute;
  top: ${({ top }) => top}px;
  transform: translateX(-50%) translateY(-50%);
  user-select: none;
  z-index: 11;
`;

export default SelectionPopover;
